import { supabase } from './supabase';

// ── Create a community ────────────────────────────────────────────────────────
export async function createCommunity({
  name, subject, description, userId,
  startDate, endDate, minQuestions, courseSubject,
}) {
  // 1. Insert community
  const insertData = {
    name,
    subject,
    description,
    created_by:     userId,
    start_date:     startDate  || new Date().toISOString(),
    end_date:       endDate    || null,
    is_locked:      false,
    min_questions:  minQuestions || null,
    course_subject: courseSubject || null,
  };

  const { data: community, error } = await supabase
    .from('communities')
    .insert(insertData)
    .select()
    .single();
  if (error) return { data: null, error };

  // 2. Auto-add creator as admin (upsert so it never silently fails on RLS conflicts)
  const { error: memberError } = await supabase
    .from('community_members')
    .upsert(
      { community_id: community.id, user_id: userId, role: 'admin' },
      { onConflict: 'community_id,user_id' }
    );

  if (memberError) {
    // Community was created but membership failed — delete the orphaned community
    await supabase.from('communities').delete().eq('id', community.id);
    return { data: null, error: memberError };
  }

  return { data: community, error: null };
}

// ── Get all communities the user belongs to ───────────────────────────────────
export async function getMyCommunities(userId) {
  const { data, error } = await supabase
    .from('community_members')
    .select(`role, joined_at, communities(
      id, name, subject, description, created_by, created_at,
      start_date, end_date, is_locked, min_questions, course_subject
    )`)
    .eq('user_id', userId);
  if (error) return { data: [], error };
  return {
    data: (data || []).map(r => ({ ...r.communities, myRole: r.role, joinedAt: r.joined_at })),
    error: null,
  };
}

// ── Update community settings (admin only) ────────────────────────────────────
export async function updateCommunity(communityId, updates) {
  // updates can include: name, subject, description, start_date, end_date,
  //                      is_locked, min_questions, course_subject
  const { data, error } = await supabase
    .from('communities')
    .update(updates)
    .eq('id', communityId)
    .select()
    .single();
  return { data, error };
}

// ── Toggle community lock (admin only) ───────────────────────────────────────
export async function toggleCommunityLock(communityId, isLocked) {
  const { data, error } = await supabase
    .from('communities')
    .update({ is_locked: isLocked })
    .eq('id', communityId)
    .select('id, is_locked')
    .single();
  return { data, error };
}

// ── Utility: derive course status from community fields ───────────────────────
export function getCourseStatus(community) {
  const now = new Date();
  if (community.is_locked) return 'locked';
  if (community.end_date && new Date(community.end_date) < now) return 'ended';
  if (community.end_date) return 'active_course';  // has end_date but not expired
  return 'open';  // regular community, no expiry
}

// ── Get student progress for a community (admin use) ─────────────────────────
// Returns per-member post counts (questions asked + content shared)
export async function getCommunityProgress(communityId) {
  // Step 1: get all members
  const { data: members, error: membersError } = await supabase
    .from('community_members')
    .select('user_id, role, joined_at')
    .eq('community_id', communityId);

  if (membersError || !members?.length) return { data: [], error: membersError };

  // Step 2: get all posts in the community
  const { data: posts } = await supabase
    .from('community_posts')
    .select('user_id, content_type, created_at')
    .eq('community_id', communityId);

  // Step 3: get user profiles
  const userIds = members.map(m => m.user_id);
  const { data: profiles } = await supabase
    .from('user_profiles')
    .select('id, display_name, email')
    .in('id', userIds);

  const profileMap = {};
  (profiles || []).forEach(p => { profileMap[p.id] = p; });

  // Step 4: compute per-user counts
  const postsByUser = {};
  (posts || []).forEach(p => {
    if (!postsByUser[p.user_id]) postsByUser[p.user_id] = { total: 0, lastActive: null };
    postsByUser[p.user_id].total += 1;
    const t = new Date(p.created_at);
    if (!postsByUser[p.user_id].lastActive || t > postsByUser[p.user_id].lastActive) {
      postsByUser[p.user_id].lastActive = t;
    }
  });

  return {
    data: members.map(m => ({
      userId:      m.user_id,
      displayName: profileMap[m.user_id]?.display_name || 'Member',
      email:       profileMap[m.user_id]?.email || '',
      role:        m.role,
      joinedAt:    m.joined_at,
      postsCount:  postsByUser[m.user_id]?.total      || 0,
      lastActive:  postsByUser[m.user_id]?.lastActive || null,
    })),
    error: null,
  };
}

// ── Get members of a community ────────────────────────────────────────────────
export async function getCommunityMembers(communityId) {
  // Step 1: get member rows (user_id + role)
  const { data: members, error } = await supabase
    .from('community_members')
    .select('user_id, role, joined_at')
    .eq('community_id', communityId);

  if (error) return { data: [], error };
  if (!members?.length) return { data: [], error: null };

  // Step 2: fetch profiles separately (avoids FK join issues)
  const userIds = members.map(m => m.user_id);
  const { data: profiles } = await supabase
    .from('user_profiles')
    .select('id, display_name, email')
    .in('id', userIds);

  const profileMap = {};
  (profiles || []).forEach(p => { profileMap[p.id] = p; });

  return {
    data: members.map(m => ({
      id:           m.user_id,
      display_name: profileMap[m.user_id]?.display_name || 'Member',
      email:        profileMap[m.user_id]?.email || '',
      role:         m.role,
      joinedAt:     m.joined_at,
    })),
    error: null,
  };
}

// ── Get posts for a community ─────────────────────────────────────────────────
// Tries to include comment count; gracefully falls back if the comments table
// hasn't been created yet (i.e. the migration SQL hasn't been run).
export async function getCommunityPosts(communityId, limit = 30) {
  // Step 1: get posts
  const { data: posts, error } = await supabase
    .from('community_posts')
    .select('*')
    .eq('community_id', communityId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) return { data: [], error };
  if (!posts?.length) return { data: [], error: null };

  // Step 2: fetch author profiles separately
  const userIds = [...new Set(posts.map(p => p.user_id).filter(Boolean))];
  const { data: profiles } = await supabase
    .from('user_profiles')
    .select('id, display_name')
    .in('id', userIds);

  const profileMap = {};
  (profiles || []).forEach(p => { profileMap[p.id] = p; });

  return {
    data: posts.map(p => ({
      ...p,
      user_profiles: profileMap[p.user_id] ? { display_name: profileMap[p.user_id].display_name } : null,
    })),
    error: null,
  };
}

// ── Post a standalone community message ───────────────────────────────────────
export async function postCommunityMessage({ communityId, userId, body }) {
  return await supabase.from('community_posts').insert({
    community_id: communityId,
    user_id:      userId,
    content_type: 'message',
    title:        body.trim().slice(0, 120),   // used as fallback label
    message:      body.trim(),
    url:          null,
    thumbnail:    null,
    content_data: null,
    subject:      null,
  });
}

// ── Get comments for a post ───────────────────────────────────────────────────
export async function getPostComments(postId, limit = 100) {
  const { data, error } = await supabase
    .from('community_post_comments')
    .select('*')
    .eq('post_id', postId)
    .order('created_at', { ascending: true })
    .limit(limit);

  if (error || !data?.length) return { data: data || [], error };

  // Fetch display names separately (user_profiles has no direct FK from this table)
  const userIds = [...new Set(data.map(c => c.user_id).filter(Boolean))];
  const { data: profiles } = await supabase
    .from('user_profiles')
    .select('id, display_name')
    .in('id', userIds);
  const profileMap = {};
  (profiles || []).forEach(p => { profileMap[p.id] = p; });

  return {
    data: data.map(c => ({
      ...c,
      user_profiles: profileMap[c.user_id] ? { display_name: profileMap[c.user_id].display_name } : null,
    })),
    error: null,
  };
}

// ── Add a comment to a post ───────────────────────────────────────────────────
export async function addPostComment({ postId, communityId, userId, body }) {
  const { data, error } = await supabase
    .from('community_post_comments')
    .insert({ post_id: postId, community_id: communityId, user_id: userId, body: body.trim() })
    .select('*')
    .single();

  if (error || !data) return { data, error };

  // Attach display name
  const { data: profile } = await supabase
    .from('user_profiles')
    .select('display_name')
    .eq('id', userId)
    .single();

  return {
    data: { ...data, user_profiles: profile ? { display_name: profile.display_name } : null },
    error: null,
  };
}

// ── Delete a comment ──────────────────────────────────────────────────────────
export async function deletePostComment(commentId) {
  return await supabase
    .from('community_post_comments')
    .delete()
    .eq('id', commentId);
}

// ── Share content to a community ──────────────────────────────────────────────
export async function shareToСommunity({ communityId, userId, content, message }) {
  return await supabase.from('community_posts').insert({
    community_id: communityId,
    user_id:      userId,
    content_type: content.content_type,
    title:        content.title,
    url:          content.url || null,
    thumbnail:    content.thumbnail || null,
    content_data: content.content_data || null,
    subject:      content.subject || null,
    message:      message || null,
  });
}

// ── Invite someone to a community ────────────────────────────────────────────
export async function inviteToСommunity({ communityId, invitedBy, email }) {
  // Check if email is already a member
  const { data: profile } = await supabase
    .from('user_profiles')
    .select('id, display_name')
    .eq('email', email.toLowerCase().trim())
    .maybeSingle();

  if (profile) {
    // Already a user — add directly as member
    const { error } = await supabase
      .from('community_members')
      .upsert({ community_id: communityId, user_id: profile.id, role: 'member' }, { onConflict: 'community_id,user_id' });
    return { type: 'joined', profile, error };
  } else {
    // Not a user — create an invite token
    const { data: invite, error } = await supabase
      .from('community_invites')
      .upsert({ community_id: communityId, invited_by: invitedBy, email: email.toLowerCase().trim() },
               { onConflict: 'community_id,email' })
      .select()
      .single();
    const inviteUrl = invite ? `${window.location.origin}?invite=${invite.token}` : null;
    return { type: 'invited', inviteUrl, error };
  }
}

// ── Accept an invite by token ─────────────────────────────────────────────────
export async function acceptInvite({ token, userId }) {
  const { data: invite } = await supabase
    .from('community_invites')
    .select('id, community_id, communities(name, subject)')
    .eq('token', token)
    .eq('status', 'pending')
    .maybeSingle();

  if (!invite) return { data: null, error: 'Invite not found or already used' };

  // Join the community
  await supabase.from('community_members')
    .upsert({ community_id: invite.community_id, user_id: userId, role: 'member' },
             { onConflict: 'community_id,user_id' });

  // Mark invite as accepted
  await supabase.from('community_invites').update({ status: 'accepted' }).eq('id', invite.id);

  return { data: invite.communities, error: null };
}

// ── Search BiteLearning users by name or email ────────────────────────────────
// NOTE: requires a Supabase RLS SELECT policy on user_profiles that lets
// authenticated users read other profiles, e.g.:
//   CREATE POLICY "authenticated users can read profiles"
//     ON user_profiles FOR SELECT TO authenticated USING (true);
export async function searchBiteLearningUsers({ query, excludeUserId, communityId, limit = 8 }) {
  if (!query || query.trim().length < 2) return { data: [], error: null };
  const q = query.trim();

  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, display_name, email')
    .or(`display_name.ilike.%${q}%,email.ilike.%${q}%`)
    .neq('id', excludeUserId)
    .limit(limit);

  if (error || !data?.length) return { data: [], error };

  // Mark users who are already members of this community
  const { data: existing } = await supabase
    .from('community_members')
    .select('user_id')
    .eq('community_id', communityId)
    .in('user_id', data.map(u => u.id));

  const memberIds = new Set((existing || []).map(m => m.user_id));
  return {
    data: data.map(u => ({ ...u, isMember: memberIds.has(u.id) })),
    error: null,
  };
}

// ── Add a BiteLearning user directly to a community ───────────────────────────
export async function addMemberToCommunity({ communityId, userId }) {
  const { error } = await supabase
    .from('community_members')
    .upsert(
      { community_id: communityId, user_id: userId, role: 'member' },
      { onConflict: 'community_id,user_id' }
    );
  return { error };
}

// ── Leave a community ─────────────────────────────────────────────────────────
export async function leaveCommunity({ communityId, userId }) {
  return await supabase.from('community_members')
    .delete()
    .eq('community_id', communityId)
    .eq('user_id', userId);
}

// ── Delete a community (creator only) ────────────────────────────────────────
export async function deleteCommunity(communityId) {
  // Supabase cascades deletes to community_members and community_posts
  // as long as those tables have ON DELETE CASCADE foreign keys.
  // If not, we delete children first to be safe.
  await supabase.from('community_post_comments').delete().eq('community_id', communityId);
  await supabase.from('community_posts').delete().eq('community_id', communityId);
  await supabase.from('community_members').delete().eq('community_id', communityId);
  return await supabase.from('communities').delete().eq('id', communityId);
}
